import React, { useEffect, useState } from 'react';
import { Candidate, SeafarerApplicationData } from '../../types';
import Button from '../Form/Button';
import { resolveFileUrl } from '../../utils/fileUrl';

/**
 * The same Seafarer Application Form as the public website (same sections,
 * same bilingual labels) — bound to the ERP candidate record. All seafarer
 * fields live in candidate.seafarerData; the normalized candidate fields
 * (name, phone, appliedPosition, experience, certificates...) are derived
 * from it on save so list/column/employee flows keep working.
 */

interface CertState { available: boolean; expiry: string; }

interface SeaServiceRow {
  companyName: string;
  vesselName: string;
  vesselType: string;
  rank: string;
  from: string;
  to: string;
  experienceYears: number | '';
}

interface SeafarerFormData {
  // Personal Information
  name: string;
  email: string;
  nationality: string;
  gender: string;
  maritalStatus: string;
  dateOfBirth: string;
  mobile1: string;
  mobile2: string;
  relativeMobile: string;
  availableFrom: string;
  homeAddress: string;
  appliedToRank: string;
  // Marine Passport
  seamanBookNo: string;
  profession: string;
  dateOfIssue: string;
  expiryDate: string;
  // Sea Service
  seaService: SeaServiceRow[];
  // ERP-only management fields
  status: string;
  source: string;
  customSource: string;
  expectedSalary: number | '';
  interviewDate: string;
  managerFeedback: string;
  notes: string;
}

/** The website form's certificate rows — `name` is what gets submitted. */
const CERT_DEFS: { key: string; label: string; name: string }[] = [
  { key: 'securityAwareness', label: 'Security Awareness / التوعية الأمنية', name: 'Security Awareness' },
  { key: 'firstAid', label: 'First Aid / Medical Studies / اسعافات الطوارئ', name: 'First Aid / Medical Studies' },
  { key: 'fireFighting', label: 'Fire Fighting / مكافحة الحرائق', name: 'Fire Fighting' },
  { key: 'personalSurvival', label: 'Personal Sea Survival Techniques / تقنيات البقاء والإنقاذ والنجاة', name: 'Personal Sea Survival Techniques' },
  { key: 'personalSafety', label: 'Personal Safety and Social Responsibilities / السلامة الشخصية والمسؤولية الاجتماعية', name: 'Personal Safety and Social Responsibilities' },
  { key: 'medicalCertificate', label: 'International Medical Certificate for Seafarers / الشهادة الطبية البحرية', name: 'International Medical Certificate for Seafarers' },
  { key: 'cocOfficers', label: 'COC (Officers only) / شهادة الكفاءة', name: 'COC (Officers)' },
  { key: 'gocDeck', label: 'GOC (Deck Officers only) / شهادة الخبرة بحرس وحفظ واستبصاف', name: 'GOC (Deck Officers)' },
  { key: 'deckEngineWatch', label: 'Deck/Engine Watch Keeping Certificate (For Bosun, AB & Mechanics) / شهادة الخبرة بحرس ومتابعة واستبصاف', name: 'Deck/Engine Watch Keeping' },
  { key: 'shipSecurity', label: 'Ship Security Officer & Rescue Boats (For Bosun, AB & Mechanics) / ضابط أمن السفن وقوارب الإنقاذ', name: 'Ship Security Officer & Rescue Boats' },
  { key: 'foodHandler', label: 'Food Handler Certificate (For Cook) / شهادات تناول الطعام / الطباخ', name: 'Food Handler' },
];

const SOURCE_OPTIONS = [
  { value: 'website_form', label: 'Website – Seafarer Form' },
  { value: 'website', label: 'Company Website' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'referral', label: 'Employee Referral' },
  { value: 'job_board', label: 'Job Board' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'new', label: 'New Application' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'interview_scheduled', label: 'Interview Scheduled' },
  { value: 'interviewed', label: 'Interviewed' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
];

const emptyRow = (): SeaServiceRow => ({
  companyName: '', vesselName: '', vesselType: '', rank: '', from: '', to: '', experienceYears: '',
});

const splitName = (full: string): [string, string] => {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  return [parts[0] || '', parts.slice(1).join(' ')];
};

const readSeafarerData = (candidate?: Candidate): SeafarerApplicationData | undefined => {
  const raw: any = candidate?.seafarerData ?? (candidate as any)?.attributes?.seafarerData;
  if (!raw) return undefined;
  // Stored as a JSON object — but if it ever arrives as a string, parse it.
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return undefined; }
  }
  return typeof raw === 'object' ? raw : undefined;
};

const initCerts = (candidate?: Candidate): CertMap => {
  const sd = readSeafarerData(candidate);
  const map: CertMap = {};
  CERT_DEFS.forEach(d => { map[d.key] = { available: false, expiry: '' }; });
  (sd?.certificates || []).forEach(c => {
    const def = CERT_DEFS.find(d => d.name === c.name);
    if (def) map[def.key] = { available: true, expiry: c.expiry || '' };
  });
  return map;
};

const initExtraCerts = (candidate?: Candidate): { name: string; expiry: string }[] => {
  const sd = readSeafarerData(candidate);
  return (sd?.certificates || [])
    .filter(c => !CERT_DEFS.some(d => d.name === c.name))
    .map(c => ({ name: c.name, expiry: c.expiry || '' }));
};

const initForm = (candidate?: Candidate): SeafarerFormData => {
  const sd = readSeafarerData(candidate);
  const source = candidate?.source || 'website_form';
  const isKnownSource = SOURCE_OPTIONS.some(o => o.value === source);
  return {
    name: candidate ? `${candidate.firstName || ''} ${candidate.lastName || ''}`.replace(/\s+/g, ' ').trim() : '',
    email: candidate?.email || '',
    nationality: candidate?.nationality || '',
    gender: sd?.gender || '',
    maritalStatus: sd?.maritalStatus || '',
    dateOfBirth: candidate?.dateOfBirth ? candidate.dateOfBirth.split('T')[0] : '',
    mobile1: candidate?.phone || '',
    mobile2: sd?.mobile2 || '',
    relativeMobile: sd?.relativeMobile || '',
    availableFrom: candidate?.availableFrom ? candidate.availableFrom.split('T')[0] : '',
    homeAddress: sd?.homeAddress || '',
    appliedToRank: candidate?.appliedPosition || '',
    seamanBookNo: sd?.marinePassport?.number || '',
    profession: sd?.marinePassport?.profession || '',
    dateOfIssue: sd?.marinePassport?.issueDate || '',
    expiryDate: sd?.marinePassport?.expiryDate || '',
    seaService: sd?.seaService?.length ? sd.seaService.map(r => ({ ...r })) : [emptyRow()],
    status: candidate?.status || 'new',
    source: isKnownSource ? source : 'other',
    customSource: isKnownSource ? '' : source,
    expectedSalary: candidate?.expectedSalary ?? '',
    interviewDate: candidate?.interviewDate ? candidate.interviewDate.split('T')[0] : '',
    managerFeedback: candidate?.managerFeedback || '',
    notes: candidate?.notes || '',
  };
};

type CertMap = Record<string, CertState>;

const fieldCls = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-600';
const labelCls = 'block text-sm font-medium text-gray-700 mb-2';
const sectionCls = 'border-b border-gray-200 pb-8';
const sectionTitle = 'text-xl font-semibold text-primary-700 mb-6';

interface SeafarerCandidateFormProps {
  candidate?: Candidate;
  readOnly?: boolean;
  onSubmit?: (data: Partial<Candidate> & { cvFile?: File; pdfFile?: File }) => void;
  onCancel?: () => void;
}

const SeafarerCandidateForm: React.FC<SeafarerCandidateFormProps> = ({ candidate, readOnly = false, onSubmit, onCancel }) => {
  const [form, setForm] = useState<SeafarerFormData>(() => initForm(candidate));
  const [certs, setCerts] = useState<CertMap>(() => initCerts(candidate));
  const [extraCerts, setExtraCerts] = useState<{ name: string; expiry: string }[]>(() => initExtraCerts(candidate));
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  // The parent may hand us a fuller record asynchronously (the details/edit
  // views re-fetch via the content-manager so seafarerData is included) —
  // re-initialize when the candidate object changes.
  useEffect(() => {
    setForm(initForm(candidate));
    setCerts(initCerts(candidate));
    setExtraCerts(initExtraCerts(candidate));
  }, [candidate]);

  const set = (field: keyof SeafarerFormData, value: any) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const setCert = (key: string, patch: Partial<CertState>) =>
    setCerts(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const setService = (index: number, field: keyof SeaServiceRow, value: any) =>
    setForm(prev => ({
      ...prev,
      seaService: prev.seaService.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const missing: string[] = [];
    if (!form.name.trim()) missing.push('Name');
    if (!form.mobile1.trim()) missing.push('Mobile 1');
    if (!form.nationality.trim()) missing.push('Nationality');
    if (!form.dateOfBirth) missing.push('Date of Birth');
    if (!form.appliedToRank.trim()) missing.push('Applied to rank');
    if (missing.length) {
      setErrors(missing);
      return;
    }
    setErrors([]);
    onSubmit?.(buildCandidatePayload());
  };

  const buildCandidatePayload = (): Partial<Candidate> & { cvFile?: File; pdfFile?: File } => {
    const [firstName, lastName] = splitName(form.name);
    const servedRows = form.seaService.filter(s => s.companyName || s.vesselName || s.rank);
    const heldCerts = CERT_DEFS
      .filter(d => certs[d.key].available)
      .map(d => ({ name: d.name, expiry: certs[d.key].expiry || '' }));
    const allCerts = [...heldCerts, ...extraCerts.filter(c => c.name.trim())];
    const totalYears = servedRows.reduce((s, r) => s + (Number(r.experienceYears) || 0), 0);
    const lastServed = servedRows[servedRows.length - 1];

    const seafarerData: SeafarerApplicationData = {
      gender: form.gender || '',
      maritalStatus: form.maritalStatus || '',
      mobile2: form.mobile2 || '',
      relativeMobile: form.relativeMobile || '',
      homeAddress: form.homeAddress || '',
      marinePassport: {
        number: form.seamanBookNo || '',
        profession: form.profession || '',
        issueDate: form.dateOfIssue || '',
        expiryDate: form.expiryDate || '',
      },
      certificates: allCerts,
      seaService: servedRows.map(s => ({ ...s, experienceYears: Number(s.experienceYears) || 0 })),
    };

    // Keep coverLetter in the website's deterministic text format — the same
    // shape it is parsed back from elsewhere.
    const details: string[] = [];
    if (form.gender || form.maritalStatus) {
      details.push(`Gender: ${form.gender || '-'} | Marital status: ${form.maritalStatus || '-'}`);
    }
    if (form.mobile2 || form.relativeMobile) {
      details.push(`Mobile 2: ${form.mobile2 || '-'} | Relative: ${form.relativeMobile || '-'}`);
    }
    if (form.homeAddress) details.push(`Address: ${form.homeAddress}`);
    if (form.seamanBookNo || form.profession) {
      details.push(
        `Marine passport: ${form.seamanBookNo || '-'} | Profession: ${form.profession || '-'}` +
        ` | Issued: ${form.dateOfIssue || '-'} | Expires: ${form.expiryDate || '-'}`
      );
    }
    if (servedRows.length) {
      details.push('Sea service:');
      servedRows.forEach(s =>
        details.push(`- ${s.companyName || '-'} / ${s.vesselName || '-'} (${s.vesselType || '-'}) — ${s.rank || '-'}, ${s.from || '?'} → ${s.to || '?'} (${s.experienceYears || 0} yrs)`)
      );
    }

    const effectiveSource = form.source === 'other' && form.customSource.trim()
      ? form.customSource.trim()
      : form.source;

    return {
      firstName,
      lastName: lastName || '-',
      email: form.email || '',
      phone: form.mobile1,
      nationality: form.nationality,
      dateOfBirth: form.dateOfBirth,
      appliedPosition: form.appliedToRank,
      appliedDate: candidate?.appliedDate || new Date().toISOString().slice(0, 10),
      source: effectiveSource,
      experience: Math.max(0, Math.round(totalYears)),
      certificates: allCerts.map(c => (c.expiry ? `${c.name} (expires ${c.expiry})` : c.name)),
      previousCompany: lastServed?.companyName || candidate?.previousCompany || '',
      expectedSalary: Number(form.expectedSalary) || 0,
      availableFrom: form.availableFrom || '',
      coverLetter: details.join('\n'),
      seafarerData,
      status: form.status as Candidate['status'],
      interviewDate: form.interviewDate || undefined,
      managerFeedback: form.managerFeedback || '',
      notes: form.notes || '',
      ...(cvFile ? { cvFile } : {}),
      ...(pdfFile ? { pdfFile } : {}),
    };
  };

  const ro = readOnly;

  return (
    <form onSubmit={handleSubmit} className="space-y-8" dir="ltr">
      {/* CV Upload */}
      <div className={sectionCls}>
        <h3 className={sectionTitle}>CV Upload / تحميل السيرة الذاتية</h3>
        {readOnly ? (
          <div className="flex space-x-4">
            {candidate?.resumeUrl && (
              <a href={resolveFileUrl(candidate.resumeUrl)} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-700 underline">View CV / عرض السيرة</a>
            )}
            {candidate?.applicationPdfUrl && (
              <a href={resolveFileUrl(candidate.applicationPdfUrl)} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-700 underline">View Application PDF</a>
            )}
            {!candidate?.resumeUrl && !candidate?.applicationPdfUrl && <span className="text-sm text-gray-400">No files uploaded</span>}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Upload CV (PDF, DOC, DOCX) / تحميل السيرة الذاتية</label>
              <div className="flex items-center space-x-3">
                <input type="file" accept=".pdf,.doc,.docx" onChange={e => setCvFile(e.target.files?.[0] || null)} className={fieldCls} />
                {candidate?.resumeUrl && !cvFile && (
                  <a href={resolveFileUrl(candidate.resumeUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-700 underline whitespace-nowrap">current CV</a>
                )}
              </div>
            </div>
            <div>
              <label className={labelCls}>Application PDF (optional)</label>
              <div className="flex items-center space-x-3">
                <input type="file" accept=".pdf" onChange={e => setPdfFile(e.target.files?.[0] || null)} className={fieldCls} />
                {candidate?.applicationPdfUrl && !pdfFile && (
                  <a href={resolveFileUrl(candidate.applicationPdfUrl)} target="_blank" rel="noopener noreferrer" className="text-xs text-primary-700 underline whitespace-nowrap">current PDF</a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Personal Information */}
      <div className={sectionCls}>
        <h3 className={sectionTitle}>Personal Information / البيانات الشخصية</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className={labelCls}>Name / الاسم <span className="text-red-500">*</span></label>
            <input type="text" disabled={ro} value={form.name} onChange={e => set('name', e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Email / البريد الإلكتروني <span className="text-gray-400 text-xs">(optional / اختياري)</span></label>
            <input type="email" disabled={ro} value={form.email} onChange={e => set('email', e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Nationality / الجنسية <span className="text-red-500">*</span></label>
            <input type="text" disabled={ro} value={form.nationality} onChange={e => set('nationality', e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Gender / الجنس</label>
            <select disabled={ro} value={form.gender} onChange={e => set('gender', e.target.value)} className={fieldCls}>
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Marital Status / الحالة الاجتماعية</label>
            <select disabled={ro} value={form.maritalStatus} onChange={e => set('maritalStatus', e.target.value)} className={fieldCls}>
              <option value="">Select</option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
              <option value="Divorced">Divorced</option>
              <option value="Widowed">Widowed</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Date of Birth / تاريخ الميلاد <span className="text-red-500">*</span></label>
            <input type="date" disabled={ro} value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Mobile 1 / الهاتف الأول <span className="text-red-500">*</span></label>
            <input type="tel" disabled={ro} value={form.mobile1} onChange={e => set('mobile1', e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Mobile 2 / الهاتف الثاني</label>
            <input type="tel" disabled={ro} value={form.mobile2} onChange={e => set('mobile2', e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Relative Mobile / هاتف الأقارب</label>
            <input type="tel" disabled={ro} value={form.relativeMobile} onChange={e => set('relativeMobile', e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Available from / متاح من تاريخ</label>
            <input type="date" disabled={ro} value={form.availableFrom} onChange={e => set('availableFrom', e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Applied to rank / الوظيفة المقدم لها <span className="text-red-500">*</span></label>
            <input type="text" disabled={ro} value={form.appliedToRank} onChange={e => set('appliedToRank', e.target.value)} className={fieldCls} placeholder="e.g., Captain, Chief Engineer, AB Seaman" />
          </div>
        </div>
        <div className="mt-6">
          <label className={labelCls}>Home Address / عنوان المنزل</label>
          <textarea disabled={ro} value={form.homeAddress} onChange={e => set('homeAddress', e.target.value)} rows={3} className={fieldCls} />
        </div>
      </div>

      {/* Marine Passport */}
      <div className={sectionCls}>
        <h3 className={sectionTitle}>Marine Passport / الجواز البحري</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className={labelCls}>Marine Passport No. / رقم الجواز البحري</label>
            <input type="text" disabled={ro} value={form.seamanBookNo} onChange={e => set('seamanBookNo', e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Profession / المهنة بالجواز البحري</label>
            <input type="text" disabled={ro} value={form.profession} onChange={e => set('profession', e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Date of Issue / تاريخ الإصدار</label>
            <input type="date" disabled={ro} value={form.dateOfIssue} onChange={e => set('dateOfIssue', e.target.value)} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Expiry Date / تاريخ الانتهاء</label>
            <input type="date" disabled={ro} value={form.expiryDate} onChange={e => set('expiryDate', e.target.value)} className={fieldCls} />
          </div>
        </div>
      </div>

      {/* Certificates */}
      <div className={sectionCls}>
        <h3 className={sectionTitle}>Certificates / الشهادات</h3>
        <div className="space-y-4">
          {CERT_DEFS.map(cert => (
            <div key={cert.key} className="grid md:grid-cols-3 gap-4 items-center p-4 bg-gray-50 rounded-lg">
              <div className="md:col-span-1">
                <label className="text-sm font-medium text-gray-700">{cert.label}</label>
              </div>
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input type="radio" name={`${cert.key}_available`} disabled={ro}
                    checked={certs[cert.key].available === true}
                    onChange={() => setCert(cert.key, { available: true })} className="mr-2" />
                  Yes / نعم
                </label>
                <label className="flex items-center">
                  <input type="radio" name={`${cert.key}_available`} disabled={ro}
                    checked={certs[cert.key].available === false}
                    onChange={() => setCert(cert.key, { available: false })} className="mr-2" />
                  No / لا
                </label>
              </div>
              <div>
                <input type="date" disabled={ro || !certs[cert.key].available}
                  value={certs[cert.key].expiry}
                  onChange={e => setCert(cert.key, { expiry: e.target.value })}
                  className={fieldCls} />
              </div>
            </div>
          ))}
          {extraCerts.map((c, i) => (
            <div key={`extra-${i}`} className="grid md:grid-cols-3 gap-4 items-center p-4 bg-gray-50 rounded-lg">
              <div className="md:col-span-1">
                <input type="text" disabled={ro} value={c.name} placeholder="Certificate name"
                  onChange={e => setExtraCerts(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                  className={fieldCls} />
              </div>
              <div className="text-sm text-gray-500">Extra certificate</div>
              <div>
                <input type="date" disabled={ro} value={c.expiry}
                  onChange={e => setExtraCerts(prev => prev.map((x, j) => j === i ? { ...x, expiry: e.target.value } : x))}
                  className={fieldCls} />
              </div>
            </div>
          ))}
          {!ro && (
            <button type="button" onClick={() => setExtraCerts(prev => [...prev, { name: '', expiry: '' }])}
              className="text-sm text-primary-700 hover:text-primary-800 font-medium">
              + Add other certificate / إضافة شهادة أخرى
            </button>
          )}
        </div>
      </div>

      {/* Sea Service Experience */}
      <div className={sectionCls}>
        <div className="flex items-center justify-between mb-6">
          <h3 className={sectionTitle.replace('mb-6', '')}>Sea Service Experience / الخبرات البحرية</h3>
          {!ro && (
            <Button type="button" variant="primary" size="sm"
              onClick={() => setForm(prev => ({ ...prev, seaService: [...prev.seaService, emptyRow()] }))}>
              + Add Row / إضافة صف
            </Button>
          )}
        </div>
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Company Name / اسم الشركة', 'Vessel Name / اسم السفينة', 'Vessel Type / نوع السفينة', 'Rank / الرتبة', 'From / من', 'To / إلى', 'Experience (Years) / الخبرة (سنوات)', 'Action / إجراء'].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {form.seaService.map((row, i) => (
                <tr key={i}>
                  <td className="px-2 py-2"><input type="text" disabled={ro} value={row.companyName} onChange={e => setService(i, 'companyName', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" /></td>
                  <td className="px-2 py-2"><input type="text" disabled={ro} value={row.vesselName} onChange={e => setService(i, 'vesselName', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" /></td>
                  <td className="px-2 py-2"><input type="text" disabled={ro} value={row.vesselType} onChange={e => setService(i, 'vesselType', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" /></td>
                  <td className="px-2 py-2"><input type="text" disabled={ro} value={row.rank} onChange={e => setService(i, 'rank', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" /></td>
                  <td className="px-2 py-2"><input type="date" disabled={ro} value={row.from} onChange={e => setService(i, 'from', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" /></td>
                  <td className="px-2 py-2"><input type="date" disabled={ro} value={row.to} onChange={e => setService(i, 'to', e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" /></td>
                  <td className="px-2 py-2"><input type="number" disabled={ro} value={row.experienceYears} onChange={e => setService(i, 'experienceYears', e.target.value === '' ? '' : Number(e.target.value))} className="w-full px-2 py-1 border border-gray-300 rounded text-sm" /></td>
                  <td className="px-2 py-2 text-center">
                    {!ro && (
                      <button type="button" disabled={form.seaService.length <= 1}
                        onClick={() => setForm(prev => ({ ...prev, seaService: prev.seaService.filter((_, j) => j !== i) }))}
                        className="text-red-500 hover:text-red-700 disabled:opacity-40">
                        <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ERP management fields */}
      <div>
        <h3 className={sectionTitle}>Management & Evaluation / الإدارة والتقييم</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <label className={labelCls}>Status / الحالة</label>
            <select disabled={ro} value={form.status} onChange={e => set('status', e.target.value)} className={fieldCls}>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Application Source / مصدر التقديم</label>
            <select disabled={ro} value={form.source} onChange={e => set('source', e.target.value)} className={fieldCls}>
              {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {form.source === 'other' && (
            <div>
              <label className={labelCls}>Custom Source</label>
              <input type="text" disabled={ro} value={form.customSource} onChange={e => set('customSource', e.target.value)} className={fieldCls} placeholder="e.g., Facebook, Walk-in..." />
            </div>
          )}
          <div>
            <label className={labelCls}>Expected Salary (USD)</label>
            <input type="number" disabled={ro} value={form.expectedSalary} onChange={e => set('expectedSalary', e.target.value === '' ? '' : Number(e.target.value))} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Interview Date</label>
            <input type="date" disabled={ro} value={form.interviewDate} onChange={e => set('interviewDate', e.target.value)} className={fieldCls} />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          <div>
            <label className={labelCls}>Manager Feedback</label>
            <textarea disabled={ro} value={form.managerFeedback} onChange={e => set('managerFeedback', e.target.value)} rows={3} className={fieldCls} />
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea disabled={ro} value={form.notes} onChange={e => set('notes', e.target.value)} rows={3} className={fieldCls} />
          </div>
        </div>
      </div>

      {!ro && (
        <>
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
              Missing required fields: {errors.join(', ')}
            </div>
          )}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button type="submit" variant="primary">{candidate ? 'Update Candidate' : 'Add Candidate'}</Button>
          </div>
        </>
      )}
    </form>
  );
};

export default SeafarerCandidateForm;
